// Layout: a glass sidebar on wide screens, a floating tab bar on phones.
import { CalendarDays, ListChecks, LogOut, Plus, SlidersHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AppUser } from '../../shared/api';
import { paths, type Route } from '../lib/router';
import { useEditor } from './EditorContext';
import { Link } from './Link';
import { Logo } from './Logo';

const NAV = [
  { key: 'today', to: paths.today, label: 'Heute', icon: ListChecks },
  { key: 'history', to: paths.history, label: 'Verlauf', icon: CalendarDays },
  { key: 'habits', to: paths.habits, label: 'Habits', icon: SlidersHorizontal },
] as const;

export function Shell({ route, user, onSignOut, children }: { route: Route; user: AppUser; onSignOut: () => void; children: ReactNode }) {
  const editor = useEditor();
  const section = route.name === 'habit' ? 'history' : route.name === 'account' ? 'habits' : route.name;
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar__panel glass">
          <Link to={paths.today} className="sidebar__brand" aria-label="Platypus Habit – Heute">
            <Logo size={38} />
            <span className="sidebar__name">
              Platypus<span>Habit</span>
            </span>
          </Link>
          <nav className="sidebar__nav" aria-label="Hauptnavigation">
            {NAV.map(({ key, to, label, icon: Icon }) => (
              <Link key={key} to={to} className="nav-item" aria-current={section === key ? 'page' : undefined}>
                <Icon size={20} aria-hidden="true" />
                {label}
              </Link>
            ))}
          </nav>
          <button type="button" className="btn btn--primary btn--block sidebar__new" onClick={() => editor.openNew()}>
            <Plus size={18} /> Neues Habit
          </button>
          <div className="sidebar__spacer" />
          <div className="sidebar__user">
            <Link to={paths.account} className="sidebar__account" aria-current={route.name === 'account' ? 'page' : undefined} title="Konto">
              <span className="avatar" aria-hidden="true">
                {(user.name || user.email).slice(0, 1).toUpperCase()}
              </span>
              <span className="sidebar__email">{user.email}</span>
            </Link>
            <button type="button" className="icon-btn" onClick={onSignOut} aria-label="Abmelden" title="Abmelden">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">{children}</main>

      <nav className="tabbar glass glass--strong" aria-label="Hauptnavigation">
        {NAV.map(({ key, to, label, icon: Icon }) => (
          <Link key={key} to={to} className="tab" aria-current={section === key ? 'page' : undefined}>
            <span className="tab__icon">
              <Icon size={22} aria-hidden="true" />
            </span>
            <span className="tab__label">{label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
